import { useEffect, useMemo, useRef, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { EFontWeights } from '@appcharge/shared-ui';
import { Divider, Grid, Stack } from '@mui/material';
import useFeatureFlags from 'api/useFeatureFlags';
import useImages from 'api/useImages';
import useOffersUI from 'api/useOffersUI';
import usePermissions from 'api/usePermissions';
import useTheme from 'api/useTheme';
import { ETaskId, OfferUI } from 'common/contracts';
import {
  EBundlesInternalViewModel,
  EFeatureFlag,
  EPopupSubType,
  ESpecialOfferInternalViewModel
} from 'constants/enums';
import { useFormik } from 'formik';
import { useChecklistState } from 'state/hooks/setup-checklist.hook';
import { permissionsUtil } from 'utils/permissionsUtil';
import { initialOfferDesignTypesItems } from 'utils/selectOptionsUtils';
import * as yup from 'yup';

import AcContentWrapper from 'components/AcContentWrapper/AcContentWrapper';
import AcViewWrapper from 'components/AcViewWrapper/AcViewWrapper';
import PageTopBar from 'components/Topbar/PageTopBar';

import { usePageTypeByFeatureFlag } from '../../../hooks/usePageTypeByFF';
import { EToasterStatus, showToast } from '../../../utils/showToast';
import { DEFAULT_COLORS, DEFAULT_NUMBERS, DEFAULT_TEXT } from '../defaults';

import OfferUiAssets from './OfferUiSections/Assets';
import OfferUiMock from './OfferUiSections/Mock';
import OfferUiSettings from './OfferUiSections/Settings';
import OfferUiTitle from './OfferUiSections/Title';
import ProgressBarUiForm from './ProgressBars';
import RollingOfferUiForm from './RollingOffers';
import { OfferDesignTypes, OffersUIFormProps } from './types';

import 'style/forms.scss';

const OffersUIForm = ({ edit = false, dup = false }: OffersUIFormProps) => {
  const { publisherId } = useParams();
  const { offerUIId } = useParams();
  const navigate = useNavigate();
  const { featureFlags } = useFeatureFlags({ publisherId });
  const { canUserEdit } = usePermissions();

  const { getGeneralTheme: generalTheme } = useTheme(publisherId);
  const { addOfferUI, updateOfferUI, getOfferUI, getOffersUI } = useOffersUI(
    publisherId,
    offerUIId
  );
  const { setTaskStatus, hasCategories, isWebStore } = useChecklistState();
  const addPictureInputRef = useRef<HTMLInputElement>(null);
  const [currentUploadingField, setCurrentUploadingField] = useState('');
  const [offerUiType, setOfferUiType] = useState(
    getOfferUI?.data?.offerUiType || OfferDesignTypes.SpecialOffer
  );
  const { getImages } = useImages(publisherId);
  const [isNameUnique, setIsNameUnique] = useState(true);
  const [isExternalIdUnique, setIsExternalIdUnique] = useState(true);
  const hasFeatureFlagProgressBar =
    featureFlags?.[EFeatureFlag.DASHBOARD_PROGRESS_BAR];
  useEffect(() => {
    if (offerUIId && publisherId) {
      getOfferUI.refetch();
    }
  }, [offerUIId, publisherId]);

  const shouldAllowTransparentPopup = useMemo(
    () =>
      generalTheme?.data?.bundlesInternalViewModel ===
      EBundlesInternalViewModel.JUNES_JOURNEY,
    [generalTheme.data?.bundlesInternalViewModel]
  );

  const updateInfoSchema = useMemo(() => {
    const rollingOfferShape = {
      backgroundColor: yup
        .object()
        .default(DEFAULT_COLORS.rollingOffer.background)
        .required('Offer background color is required'),
      backgroundOpacity: yup
        .number()
        .default(DEFAULT_NUMBERS.opacity)
        .min(0)
        .max(100)
        .required(),
      subRollingOffer: yup.object().shape({
        backgroundColor: yup
          .object()
          .default(DEFAULT_COLORS.rollingOffer.subBackground)
          .required(),
        lockImage: yup.string().default('https://...'),
        collectText: yup.string().required().min(2).max(12)
      })
    };

    const progressBarShape = {
      barFillColor: yup
        .object()
        .default(DEFAULT_COLORS.progressBar.barFill)
        .required(),
      barRadius: yup.number().default(DEFAULT_NUMBERS.radius).min(0).required(),
      backgroundOpacity: yup
        .number()
        .default(DEFAULT_NUMBERS.opacity)
        .min(0)
        .max(100)
        .required(),
      accumulatedItem: yup
        .object({
          imageUrl: yup.string().default('').required(),
          backgroundColor: yup
            .object()
            .default(DEFAULT_COLORS.progressBar.accumulatedItem.backgroundColor)
            .required(),
          backgroundImageUrl: yup.string().default('')
        })
        .required(),
      backgroundColor: yup
        .object()
        .default(DEFAULT_COLORS.progressBar.background)
        .required(),
      frameRadius: yup
        .number()
        .default(DEFAULT_NUMBERS.radius)
        .min(0)
        .required(),
      textColor: yup
        .string()
        .default(DEFAULT_COLORS.progressBar.textColor)
    };

    const specialOfferShape = {
      collectText: yup.string().when(['$offerUiType'], {
        is: OfferDesignTypes.SpecialOffer,
        then: yup
          .string()
          .required('Free Offer Default Text is required')
          .max(8, 'Free Offer Default Text cannot exceed 8 characters'),
        otherwise: yup.string()
      }),
      subTitle: yup.object().shape({
        text: yup
          .string()
          .test('max-lines', 'Maximum of two lines allowed', (value) => {
            if (!value) return true;
            return (value.match(/\n/g) || []).length <= 1;
          })
      })
    };

    return yup.object().shape({
      name: yup.string().required('Name is required'),
      externalId: yup
        .string()
        .required('External Id is required')
        .matches(/^\S+$/, 'External ID cannot contain spaces'),
      backgroundImage: yup.string().when('offerUiType', {
        is: (offerUiType: OfferDesignTypes) =>
          offerUiType !== OfferDesignTypes.RollingOffer &&
          offerUiType !== OfferDesignTypes.ProgressBar &&
          !shouldAllowTransparentPopup,
        then: yup.string().required('Background image is required'),
        otherwise: yup.string().notRequired()
      }),
      buttonSuffixImage: yup.string(),
      buttonSuffixAnimation: yup.string(),
      badgeCoverImage: yup.string().when('offerUiType', {
        is: (offerUiType: OfferDesignTypes) =>
          offerUiType === OfferDesignTypes.PopUp,
        then: yup.string().notRequired()
      }),
      offerUiType: yup.string().required('Offer UI Type is required'),
      specialOffer: yup.object().when('offerUiType', {
        is: (offerUiType: OfferDesignTypes) =>
          offerUiType !== OfferDesignTypes.Bundle,
        then: yup.object().shape(specialOfferShape)
      }),
      borderWidth: yup.mixed().when('offerUiType', {
        is: (offerUiType: OfferDesignTypes) =>
          offerUiType === OfferDesignTypes.RollingOffer,
        then: yup
          .number()
          .typeError('Border width must be a number')
          .min(0, 'Border width must be minimum 0px')
          .max(8, 'Border width cannot exceed 8px')
          .required('Border width is required for Rolling Offer'),
        otherwise: yup
          .string()
          .matches(/^[0-9]*$/, 'Border width cannot contain letters')
      }),
      rollingOffer: yup.object().when('offerUiType', {
        is: OfferDesignTypes.RollingOffer,
        then: yup.object().shape(rollingOfferShape),
        otherwise: yup.object().notRequired()
      }),
      progressBar: yup.object().when('offerUiType', {
        is: OfferDesignTypes.ProgressBar,
        then: yup.object().shape(progressBarShape),
        otherwise: yup.object().notRequired()
      })
    });
  }, [shouldAllowTransparentPopup]);

  const initialValues = useMemo(() => {
    let dataByOfferType: any = {};

    switch (offerUiType) {
      case OfferDesignTypes.RollingOffer: {
        const rollingOffer = getOfferUI?.data?.rollingOffer || {};
        const subRollingOffer = rollingOffer?.subRollingOffer || {};

        dataByOfferType = {
          borderColor:
            getOfferUI.data?.borderColor?.colorOne != null
              ? getOfferUI.data.borderColor
              : DEFAULT_COLORS.specialOffer.mainColor,
          borderWidth: getOfferUI.data?.borderWidth ?? 0,
          rollingOffer: {
            arrowColor: rollingOffer.arrowColor || '#ffffff',
            arrowImage: rollingOffer.arrowImage || '',
            backgroundColor:
              rollingOffer.backgroundColor ||
              DEFAULT_COLORS.rollingOffer.background,
            headerImage: rollingOffer.headerImage || '',
            backgroundOpacity:
              rollingOffer.backgroundOpacity ?? DEFAULT_NUMBERS.opacity,
            collectBehavior: 'disappear',
            enableScroll: rollingOffer.enableScroll ?? false,
            scrollArrowImage: rollingOffer.scrollArrowImage || '',
            subRollingOffer: {
              backgroundColor:
                subRollingOffer.backgroundColor ||
                DEFAULT_COLORS.rollingOffer.subBackground,
              ...(subRollingOffer.backgroundImage && {
                backgroundImage: subRollingOffer.backgroundImage
              }),
              lockImage:
                subRollingOffer.lockImage ||
                DEFAULT_COLORS.rollingOffer.lockImage,
              collectText: subRollingOffer.collectText || DEFAULT_TEXT.free
            }
          }
        };
        break;
      }

      case OfferDesignTypes.SpecialOffer:
      case OfferDesignTypes.PopUp: {
        const specialOffer = getOfferUI.data?.specialOffer || {};

        dataByOfferType = {
          borderColor:
            getOfferUI.data?.borderColor ||
            DEFAULT_COLORS.specialOffer.mainColor,
          borderWidth: getOfferUI.data?.borderWidth ?? 0,
          specialOffer: {
            templateType: specialOffer.templateType || DEFAULT_TEXT.single,
            presentOfferEndTimer: false,
            backgroundColor:
              specialOffer.backgroundColor ||
              DEFAULT_COLORS.specialOffer.background,
            title: specialOffer.title || '',
            fontSize: specialOffer.fontSize || DEFAULT_NUMBERS.fontSize,
            fontWeight: specialOffer.fontWeight || DEFAULT_TEXT.normal,
            fontColor:
              specialOffer.fontColor || DEFAULT_COLORS.specialOffer.mainColor,
            subTitle: {
              text: specialOffer.subTitle?.text || '',
              fontSize:
                specialOffer.subTitle?.fontSize || DEFAULT_NUMBERS.subFontSize,
              fontWeight:
                specialOffer.subTitle?.fontWeight || DEFAULT_TEXT.normal,
              fontColor:
                specialOffer.subTitle?.fontColor ||
                DEFAULT_COLORS.specialOffer.mainColor
            },
            ...(offerUiType === OfferDesignTypes.SpecialOffer && {
              collectText: specialOffer.collectText || DEFAULT_TEXT.collect
            })
          },
          ...(offerUiType === OfferDesignTypes.PopUp &&
          getOfferUI?.data?.badgeCoverImage
            ? { badgeCoverImage: getOfferUI?.data?.badgeCoverImage }
            : {})
        };
        break;
      }

      case OfferDesignTypes.ProgressBar: {
        const progressBar = getOfferUI?.data?.progressBar || {};

        dataByOfferType = {
          borderColor:
            getOfferUI.data?.borderColor || DEFAULT_COLORS.progressBar.border,
          borderWidth:
            getOfferUI.data?.borderWidth ?? DEFAULT_NUMBERS.borderWidth,
          progressBar: {
            barFillColor:
              progressBar.barFillColor || DEFAULT_COLORS.progressBar.barFill,
            barRadius: progressBar.barRadius ?? DEFAULT_NUMBERS.radius,
            accumulatedItem: {
              imageUrl: progressBar.accumulatedItem?.imageUrl || '',
              backgroundColor:
                progressBar.accumulatedItem?.backgroundColor ||
                DEFAULT_COLORS.progressBar.accumulatedItem.backgroundColor,
              backgroundImageUrl:
                progressBar.accumulatedItem?.backgroundImageUrl ||
                DEFAULT_COLORS.progressBar.accumulatedItem.backgroundImageUrl
            },
            backgroundOpacity:
              progressBar.backgroundOpacity ?? DEFAULT_NUMBERS.opacity,
            backgroundColor:
              progressBar.backgroundColor ??
              DEFAULT_COLORS.progressBar.background,
            frameRadius: progressBar.frameRadius ?? DEFAULT_NUMBERS.radius,
            textColor:
              progressBar.textColor ?? DEFAULT_COLORS.progressBar.textColor
          }
        };
        break;
      }

      default:
        break;
    }

    return {
      offerUiType,
      name: getOfferUI.data?.name
        ? `${getOfferUI.data.name}${dup ? '_copy' : ''}`
        : '',
      description: 'N/A',
      backgroundImage: getOfferUI.data?.backgroundImage || '',
      horizontalBackgroundImage:
        getOfferUI.data?.horizontalBackgroundImage || '',
      buttonSuffixImage: getOfferUI.data?.buttonSuffixImage || '',
      buttonSuffixAnimation: getOfferUI.data?.buttonSuffixAnimation || '',
      ...dataByOfferType,
      externalId: getOfferUI.data?.externalId
        ? getOfferUI.data.externalId.trim().replace(/\s+/g, '_')
        : getOfferUI.data?.name
          ? getOfferUI.data.name.trim().replace(/\s+/g, '_')
          : ''
    };
  }, [offerUiType, getOfferUI.data]);

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    submitForm,
    isValid,
    setFieldValue
  } = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: updateInfoSchema,
    onSubmit: async (values) => {
      const newOfferUI: OfferUI = {
        active: true,
        offerUiType,
        name: values.name,
        externalId: values.externalId
          ? values.externalId.trim().replace(/\s+/g, '_')
          : values.name.trim().replace(/\s+/g, '_'),
        description: 'description',
        backgroundImage: values.backgroundImage,
        horizontalBackgroundImage: values.horizontalBackgroundImage || '',
        buttonSuffixImage: values.buttonSuffixImage,
        buttonSuffixAnimation: values.buttonSuffixAnimation,
        ...(values.offerUiType === OfferDesignTypes.PopUp &&
        values.badgeCoverImage
          ? { badgeCoverImage: values.badgeCoverImage }
          : {}),
        borderColor: values.borderColor,
        borderWidth: Number(values.borderWidth)
      };
      newOfferUI.externalId = values?.externalId || null;

      if (
        (values?.offerUiType !== OfferDesignTypes.Bundle ||
          values?.specialOffer?.backgroundColor !==
            initialValues.specialOffer?.backgroundColor) &&
        values?.offerUiType !== OfferDesignTypes.RollingOffer &&
        values?.offerUiType !== OfferDesignTypes.ProgressBar
      ) {
        newOfferUI.specialOffer = {
          templateType: values?.specialOffer?.templateType,
          presentOfferEndTimer: values?.specialOffer?.presentOfferEndTimer,
          title: values?.specialOffer?.title,
          fontSize:
            Number(values?.specialOffer?.fontSize) || DEFAULT_NUMBERS.fontSize,
          fontWeight: values?.specialOffer?.fontWeight as EFontWeights,
          fontColor:
            values?.specialOffer?.fontColor ||
            DEFAULT_COLORS.specialOffer.mainColor,
          subTitle: values?.specialOffer?.subTitle || {
            fontSize: DEFAULT_NUMBERS.subFontSize,
            fontColor: DEFAULT_COLORS.specialOffer.mainColor
          },
          backgroundColor: values.specialOffer?.backgroundColor,
          ...(values?.offerUiType === OfferDesignTypes.SpecialOffer &&
            values?.specialOffer?.collectText && {
              collectText: values.specialOffer.collectText
            })
        };
      }

      if (newOfferUI.offerUiType === OfferDesignTypes.PopUp) {
        newOfferUI.offerUiSubType =
          newOfferUI.offerUiSubType || EPopupSubType.DAILY_BONUS;
      }

      if (newOfferUI.offerUiType === OfferDesignTypes.RollingOffer) {
        newOfferUI.rollingOffer = {
          arrowColor: values.rollingOffer?.arrowColor || '#ffffff',
          arrowImage: values.rollingOffer?.arrowImage || '',
          backgroundColor:
            values.rollingOffer?.backgroundColor ||
            DEFAULT_COLORS.specialOffer.background,
          headerImage: values.rollingOffer?.headerImage || '',
          backgroundOpacity:
            values.rollingOffer?.backgroundOpacity ?? DEFAULT_NUMBERS.opacity,
          collectBehavior: 'disappear',
          enableScroll: values.rollingOffer?.enableScroll ?? false,
          scrollArrowImage: values.rollingOffer?.scrollArrowImage || '',
          subRollingOffer: {
            backgroundColor: {
              colorOne:
                values.rollingOffer?.subRollingOffer?.backgroundColor
                  ?.colorOne || '#ffffff',
              colorTwo:
                values.rollingOffer?.subRollingOffer?.backgroundColor
                  ?.colorTwo || '',
              direction:
                values.rollingOffer?.subRollingOffer?.backgroundColor
                  ?.gradientDirection || ''
            },
            ...(values.rollingOffer?.subRollingOffer?.backgroundImage && {
              backgroundImage:
                values.rollingOffer?.subRollingOffer?.backgroundImage
            }),
            lockImage:
              values.rollingOffer?.subRollingOffer?.lockImage ||
              'https://media.appcharge.com/defaults/lock_icon.png',
            collectText:
              values.rollingOffer?.subRollingOffer?.collectText ||
              DEFAULT_TEXT.free
          }
        };
      }

      if (newOfferUI.offerUiType === OfferDesignTypes.ProgressBar) {
        newOfferUI.backgroundColor = values.progressBar?.backgroundColor;
        newOfferUI.progressBar = {
          ...values.progressBar
        };
      }

      if (edit && offerUIId && !dup) {
        updateOfferUI.mutate(
          { offerId: offerUIId, form: newOfferUI },
          {
            onSuccess: () => {
              showToast({
                message: 'Offer Design saved successfully',
                status: EToasterStatus.SUCCESS
              });
              getOfferUI.refetch();
              navigate('../');
            },
            onError: () => {
              showToast({
                message: 'Error editing Offer Design',
                status: EToasterStatus.ERROR
              });
            }
          }
        );
      } else {
        addOfferUI.mutate(newOfferUI, {
          onSuccess: () => {
            showToast({
              message: 'Offer Design added successfully',
              status: EToasterStatus.SUCCESS
            });
            if (hasCategories && isWebStore) {
              setTaskStatus(ETaskId.CREATE_OFFER_DESIGN);
            }
            getOffersUI.refetch();
            navigate('../');
          },
          onError: () => {
            showToast({
              message: 'Error adding Offer Design',
              status: EToasterStatus.ERROR
            });
          }
        });
      }
    }
  });

  useEffect(() => {
    if (!edit && getOffersUI.data?.result) {
      const { result } = getOffersUI.data;

      const checkUniqueness = (key: keyof OfferUI, value: any) =>
        !result.some((offerUI: OfferUI) => offerUI[key] === value);

      setIsNameUnique(checkUniqueness('name', values.name));
      setIsExternalIdUnique(checkUniqueness('externalId', values.externalId));
    }
  }, [getOffersUI.data, edit, values.name, values.externalId]);

  useEffect(() => {
    if (getOfferUI?.data?.offerUiType && edit) {
      setOfferUiType(getOfferUI.data.offerUiType);
    }
  }, [getOfferUI?.data?.offerUiType]);

  const { isPageDisabled } = usePageTypeByFeatureFlag('Rolling');

  const offerDesignTypesItems = initialOfferDesignTypesItems.filter(
    (item) =>
      (item.key !== OfferDesignTypes.RollingOffer || !isPageDisabled) &&
      (item.key !== OfferDesignTypes.ProgressBar || hasFeatureFlagProgressBar)
  );

  const shouldDisplaySubTitle = useMemo(
    () =>
      generalTheme?.data?.specialOffersInternalViewModel ===
        ESpecialOfferInternalViewModel.JACKPOT_SLOTS ||
      generalTheme?.data?.specialOffersInternalViewModel ===
        ESpecialOfferInternalViewModel.BIG_FISH_CASINO,
    [generalTheme.data?.specialOffersInternalViewModel]
  );

  const formikProps = {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    submitForm,
    isValid,
    setFieldValue
  };
  return (
    <AcViewWrapper
      header={
        <PageTopBar
          disable={false}
          headline={`${edit ? 'Edit' : 'New'} Offer Design ${
            edit ? `(${getOfferUI.data ? getOfferUI.data._id : '...'})` : ''
          }`}
          buttons={[
            {
              text: 'Save',
              action: submitForm,
              disabled: !canUserEdit || !isValid
            }
          ]}
          backFunction={() => navigate('../')}
        />
      }
    >
      <AcContentWrapper className="formContent">
        {!getImages.isLoading && getImages.data && (
          <Grid container pt={'24px'}>
            <Grid item xs={5}>
              <Stack>
                <OfferUiSettings
                  {...formikProps}
                  isNameUnique={isNameUnique}
                  isExternalIdUnique={isExternalIdUnique}
                  offerDesignTypesItems={offerDesignTypesItems}
                  setOfferUiType={setOfferUiType}
                />
                <Divider />
                {values?.offerUiType === OfferDesignTypes.RollingOffer ? (
                  <RollingOfferUiForm
                    {...formikProps}
                    addPictureInputRef={addPictureInputRef}
                    getImages={getImages}
                    setCurrentUploadingField={setCurrentUploadingField}
                    currentUploadingField={currentUploadingField}
                    dup={dup}
                  />
                ) : values?.offerUiType === OfferDesignTypes.ProgressBar ? (
                  <ProgressBarUiForm
                    {...formikProps}
                    addPictureInputRef={addPictureInputRef}
                    getImages={getImages}
                    setCurrentUploadingField={setCurrentUploadingField}
                    currentUploadingField={currentUploadingField}
                    dup={dup}
                  />
                ) : (
                  <>
                    <OfferUiAssets
                      {...formikProps}
                      addPictureInputRef={addPictureInputRef}
                      getImages={getImages}
                      setCurrentUploadingField={setCurrentUploadingField}
                      currentUploadingField={currentUploadingField}
                      dup={dup}
                    />
                    <Divider />
                    <OfferUiTitle
                      {...formikProps}
                      shouldDisplaySubTitle={shouldDisplaySubTitle}
                    />
                  </>
                )}
              </Stack>
            </Grid>
            <Grid item xs={7} className="iphone-mock-wrapper">
              {!generalTheme.isLoading && (!edit || !getOfferUI.isLoading) && (
                <OfferUiMock values={values} />
              )}
            </Grid>
          </Grid>
        )}
      </AcContentWrapper>
    </AcViewWrapper>
  );
};

export default OffersUIForm;
